import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Pagination } from "../../features/web3/hyperliquid/components/Pagination";
import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { Skeleton } from "../../components/ui/skeleton";
import { cn } from "../../lib/utils";
import { formatDateTime } from "../../lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";

import { QueueTable } from "../../features/web3/forex/components/table";
import { FilterBar, FilterField, Money } from "../forex/parts";
import { PAGE_SIZES as PAGE_SIZE_OPTIONS, useCan, useForexAdmin, useOperator } from "./api";

export { Money, orDash, FilterBar, FilterField, inputCls, labelCls } from "../forex/parts";

export const BASE = "/forex-admin";

// A null MT5 figure means "the mirror could not say", never zero.
export const Unavail = () => (
  <span className="text-muted-foreground" title="Trading figures unavailable right now">
    n/a
  </span>
);

// datetime-local is the operator's local time; the API wants UTC ending in Z.
export const toUtc = (v) => (v ? new Date(v).toISOString() : "");

export const mono = "whitespace-nowrap font-mono";
export const muted = "whitespace-nowrap font-mono text-muted-foreground";

// Every admin list pages as { page, limit, total }.
export function AdminPager({ data, onChange, label }) {
  if (!data) return null;
  return (
    <Pagination
      data={{
        page: data.page,
        limit: data.limit,
        total: data.total,
        totalPages: Math.max(1, Math.ceil(data.total / data.limit)),
      }}
      onChange={onChange}
      label={label}
    />
  );
}

export function Stat({ label, children, tone }) {
  return (
    <div className={cn("rounded-xl border border-border/60 bg-white/[0.02] px-4 py-3", tone)}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 font-mono text-lg">{children}</div>
    </div>
  );
}

export function Panel({ title, action, children }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

// A panel's own loading / error state, so one failed call never blanks the page.
export function QueryState({ q, children }) {
  if (q.error) return <ErrorBanner error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return <Skeleton className="h-24 w-full" />;
  return children(q.data);
}

export const linkCls = "hover:text-primary hover:underline";

// Links to another area's page are plain text when the role does not open it (025).
export function AccountLink({ id, children }) {
  const can = useCan();
  if (!id) return children ?? "—";
  if (!can("accounts.read")) return children ?? "—";
  return (
    <Link to={`${BASE}/accounts/${id}`} className={linkCls}>
      {children}
    </Link>
  );
}

export function UserLink({ username }) {
  const can = useCan();
  if (!username) return "—";
  if (!can("users.read")) return username;
  return (
    <Link to={`${BASE}/users/${encodeURIComponent(username)}`} className={linkCls}>
      {username}
    </Link>
  );
}

// Draft/applied filters plus page for a list. Filters start from the URL so a
// cross-link like ?login=101895 lands pre-filtered; only keys in `empty` count.
export function useListState(empty) {
  const [search] = useSearchParams();
  const initial = Object.fromEntries(
    Object.keys(empty).map((k) => [k, search.get(k) ?? empty[k]]),
  );
  const [draft, setDraft] = useState(initial);
  const [applied, setApplied] = useState(initial);
  const [page, setPage] = useState(1);
  return {
    draft,
    setDraft,
    applied,
    setApplied,
    page,
    setPage,
    apply: () => {
      setApplied(draft);
      setPage(1);
    },
    reset: () => {
      setDraft(empty);
      setApplied(empty);
      setPage(1);
    },
    params: { ...applied, page },
  };
}

// An operator id to their grant name, from GET /operators/admin (025), which only
// operators.manage may read. Without it, or for a revoked operator, undefined.
function useOperatorName() {
  const can = useCan();
  const list = useForexAdmin(can("operators.manage") ? "/operators/admin" : null, undefined, {
    staleTime: 5 * 60_000,
  });
  return (id) => list.data?.find((o) => o.operatorId === id)?.name;
}

// createdByOperatorId (013): null = the owner opened it; otherwise the operator
// who opened it with POST /masters/admin.
export function OpenedBy({ id }) {
  const me = useOperator().data?.operatorId;
  const nameOf = useOperatorName();
  if (!id) return "User";
  if (id === me) return "You";
  return <span title={id}>{nameOf(id) ?? `Operator ${id}`}</span>;
}

// Uuids are long, so show the first block and keep the whole id in the tooltip.
const shortId = (id) => id.slice(0, 8);

export function ConnectionLink({ id }) {
  const can = useCan();
  if (!id) return "—";
  if (!can("connections.read")) return <span title={id}>{shortId(id)}</span>;
  return <Link to={`${BASE}/connections/${id}`} className={linkCls} title={id}>{shortId(id)}</Link>;
}

// No period page exists; its settlements are the closest view of one.
export const PeriodLink = ({ id }) =>
  id ? (
    <Link to={`${BASE}/settlements?tab=settlements&period=${id}`} className={linkCls} title={id}>{shortId(id)}</Link>
  ) : (
    "—"
  );

// Who paused copying, hid a master or resolved a problem: an operator id, named
// when the operators list is open to the signed-in operator.
export function PausedBy({ id }) {
  const me = useOperator().data?.operatorId;
  const nameOf = useOperatorName();
  if (!id) return "—";
  if (id === me) return "you";
  return <span title={id}>{nameOf(id) ?? `operator ${id}`}</span>;
}

// Money-like figure that may be null because the mirror did not answer.
export const Fig = ({ v }) => (v == null ? <Unavail /> : <Money value={v} />);

export function TabStrip({ tabs, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {tabs.map((t) => (
        <button
          key={t.value}
          type="button"
          onClick={() => onChange(t.value)}
          className={cn(
            "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
            value === t.value
              ? "border-ginox-green/60 bg-ginox-green/10 text-ginox-green"
              : "border-border/70 bg-white/[0.02] text-muted-foreground hover:text-foreground",
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// Filters + table + pager for one admin list. `fields` describe the filter
// inputs ({ name, label, options?, type? }); a datetime-local field is sent as
// UTC. `columns` may be a function of the applied filters, and `row` gets
// them as its second argument. `fixed` params always go (e.g. an account id). `paged: false` is for the
// few routes that refuse page/limit. `head` renders above the table (totals).
export function ListView({
  path,
  empty,
  fields,
  columns,
  row,
  fixed,
  paged = true,
  rowsOf = (d) => d.entries,
  head,
  label,
  emptyText,
  queryOpts,
}) {
  const s = useListState(paged ? { limit: "50", ...empty } : empty);
  const params = { ...fixed };
  for (const [k, v] of Object.entries(s.applied)) {
    const f = fields.find((x) => x.name === k);
    params[k] = f?.type === "datetime-local" ? toUtc(v) : v;
  }
  if (paged) params.page = s.page;
  // `queryOpts` is for the rare list that polls (the copy engine's problems).
  const q = useForexAdmin(path, params, { placeholderData: (prev) => prev, ...queryOpts });

  const allFields = paged
    ? [...fields, { name: "limit", label: "Page size", options: PAGE_SIZE_OPTIONS }]
    : fields;

  return (
    <div className="space-y-5">
      {allFields.length > 0 && (
        <FilterBar onApply={s.apply} onReset={s.reset}>
          {allFields.map((f) => (
            <FilterField key={f.name} {...f} draft={s.draft} setDraft={s.setDraft} />
          ))}
        </FilterBar>
      )}
      {/* A 503 stays an error, never an empty table. */}
      {q.error ? (
        <ErrorBanner error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <>
          {head && q.data ? head(q.data) : null}
          <QueueTable
            columns={typeof columns === "function" ? columns(s.applied) : columns}
            isLoading={q.isLoading}
            rows={q.data ? rowsOf(q.data) : undefined}
            renderRow={(r) => row(r, s.applied)}
            emptyText={emptyText}
          />
          {paged && <AdminPager data={q.data} onChange={s.setPage} label={label} />}
        </>
      )}
    </div>
  );
}

// Current tab lives in ?tab= so links (e.g. from the overview alerts) can open
// one directly. Switching tab drops the other params: they were that tab's.
export function useTab(fallback) {
  const [search, setSearch] = useSearchParams();
  return [search.get("tab") || fallback, (tab) => setSearch({ tab })];
}

// The record open in a side drawer lives in ?open=, so a link from another
// page (a balance operation's transfer, say) can open it directly.
export function useOpen(key = "open") {
  const [search, setSearch] = useSearchParams();
  const set = (id) => {
    const next = new URLSearchParams(search);
    if (id) next.set(key, id);
    else next.delete(key);
    setSearch(next);
  };
  return [search.get(key), set];
}

export function Drawer({ open, onClose, title, children }) {
  return (
    <Sheet open={!!open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-6">{children}</div>
      </SheetContent>
    </Sheet>
  );
}

// One MT5 balance deal behind a transfer or a charge. null = no ticket, or the
// mirror holds no deal on it: a half-finished move reads as half finished.
export function DealCard({ label, deal }) {
  return (
    <div className="rounded-xl border border-border/60 bg-white/[0.02] px-4 py-3 text-sm">
      <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">{label}</div>
      {deal ? (
        <div className="mt-2 grid grid-cols-2 gap-1 font-mono text-xs">
          <span className="text-muted-foreground">Deal</span><span>{deal.deal}</span>
          <span className="text-muted-foreground">Login</span><span>{deal.login}</span>
          {/* MT5's deal action number: 2 is a balance deal. */}
          <span className="text-muted-foreground">Action</span><span>{deal.action}</span>
          <span className="text-muted-foreground">Amount</span><Money value={deal.amount} />
          <span className="text-muted-foreground">Comment</span><span>{deal.comment}</span>
          <span className="text-muted-foreground">Time</span><span>{formatDateTime(deal.time)}</span>
        </div>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">No deal.</p>
      )}
    </div>
  );
}
